import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import type { MainData, AboutData, InstructionData } from '../types/apiData';

interface RobibetDataContextType {
  mainData: MainData | null;
  aboutData: AboutData | null;
  instructionData: InstructionData | null;
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
}

const RobibetDataContext = createContext<RobibetDataContextType>({
  mainData: null,
  aboutData: null,
  instructionData: null,
  loading: true,
  error: null,
  fetchData: async () => {
    // Placeholder
  },
});

export const useRobibetData = (): RobibetDataContextType => useContext(RobibetDataContext);

// Base storage URL for images
export const ROBIBET_STORAGE_URL = 'https://partner.robibat.com/storage/';
export const ROBIBET_API_URL = 'https://partner.robibat.com/api/index';

interface RobibetDataProviderProps {
  children: ReactNode;
}

export const RobibetDataProvider: FC<RobibetDataProviderProps> = ({ children }) => {
  const [mainData, setMainData] = useState<MainData | null>(null);
  const [aboutData, setAboutData] = useState<AboutData | null>(null);
  const [instructionData, setInstructionData] = useState<InstructionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(ROBIBET_API_URL, {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Robibet partner data');
      }

      const json = await response.json();

      setMainData(json.main || null);
      setAboutData(json.about || null);
      setInstructionData(json.instruction || null);
    } catch (err) {
      console.error('Robibet API error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const value = {
    mainData,
    aboutData,
    instructionData,
    loading,
    error,
    fetchData,
  };

  return <RobibetDataContext.Provider value={value}>{children}</RobibetDataContext.Provider>;
};
